import {Helmet} from '@dr.pogodin/react-helmet';
import {addDays, startOfToday} from 'date-fns';
import {DataStatus, FormSubmitHandler} from 'platform/components';

import {DeepPartial} from 'react-hook-form';
import {useParams} from 'react-router-dom';

import {isNotNil} from 'ramda-adjunct';

import i18n from '@dms/i18n';
import {useGetCurrentBranch, useGetDocumentData} from '@dms/shared';

import {getApiDateString, useQueryState} from 'shared/web';

import {CreateCorrectiveTaxDocumentForm} from '../../components/CreateCorrectiveTaxDocumentForm';
import {AccountingDocumentFormValues} from '../../types';
import {getBankAccountDetails} from '../../utils/getBankAccountDetails';
import {getDefaultBankAccountFromBranch} from '../../utils/getDefaultBankAccountFromBranch';
import {useGetBalanceInvoiceCorrectiveItems} from './hooks/useGetBalanceInvoiceCorrectiveItems';
import {useHandleSubmitCheckoutCorrectiveDocument} from './hooks/useHandleSubmitCheckoutCorrectiveDocument';
import {useHandleSubmitCorrectiveTaxDocument} from './hooks/useHandleSubmitCorrectiveTaxDocument';
import {mapInvoiceItems} from './utils/mapInvoiceItems';

const CORRECTIVE_TAX_DUE_DAYS = 14;

export function CreateCorrectiveTaxDocument() {
  const {id} = useParams();
  const [invoiceType] = useQueryState('invoiceType');
  const [checkoutId] = useQueryState('checkoutId');
  const [type] = useQueryState('type');

  const isFromCheckout = isNotNil(checkoutId);
  const isBalanceInvoiceFromCheckout = isFromCheckout && invoiceType === 'BALANCE_INVOICE';
  const isFull = type === 'full';

  const [createCorrectiveTaxDocument] = useHandleSubmitCorrectiveTaxDocument();
  const [createCheckoutCorrectiveTaxDocument] = useHandleSubmitCheckoutCorrectiveDocument();
  const {
    data: accountingDocument,
    isError: isInvoiceError,
    isLoading: isLoadingInvoice,
  } = useGetDocumentData(invoiceType, id);

  const {balanceInvoiceItems, isLoadingBalanceItems, isBalanceItemsError} =
    useGetBalanceInvoiceCorrectiveItems(isBalanceInvoiceFromCheckout, id);

  const handleFormSubmit: FormSubmitHandler<AccountingDocumentFormValues> = async (formData) => {
    isFromCheckout
      ? await createCheckoutCorrectiveTaxDocument(formData)
      : await createCorrectiveTaxDocument(formData);
  };

  const originBankAccount = accountingDocument?.paymentInfo?.bankAccount;
  const currentBranchResult = useGetCurrentBranch({refetchOnFocus: true});
  const defaultBankAccountResult = getDefaultBankAccountFromBranch(
    originBankAccount,
    currentBranchResult.data,
    currentBranchResult.isSynced,
    false
  );

  const bankAccountDetails = getBankAccountDetails(
    defaultBankAccountResult.selectedBankAccount,
    defaultBankAccountResult.availableBankAccounts
  );

  const today = startOfToday();
  const todayDate = getApiDateString(today);
  const defaultValues: DeepPartial<AccountingDocumentFormValues> = {
    additionalCustomer: isNotNil(accountingDocument?.additionalCustomer?.customer.id)
      ? {
          ...accountingDocument?.additionalCustomer,
          customer: {
            tradeName: null,
            ...accountingDocument?.additionalCustomer?.customer,
            id: accountingDocument?.additionalCustomer?.customer.id,
          },
        }
      : null,
    currency: accountingDocument?.currency,
    customer: accountingDocument?.customer,
    customFieldsPayload: accountingDocument?.customFields,
    issueDate: todayDate,
    dueDate: getApiDateString(
      addDays(today, accountingDocument?.dueSinceIssueDateInDays ?? CORRECTIVE_TAX_DUE_DAYS)
    ),
    dateOfTaxableSupply: todayDate,
    dateOfReception: accountingDocument?.dateOfReception,
    internalNote: accountingDocument?.internalNote,
    paymentInfo: {
      ...accountingDocument?.paymentInfo,
      bankAccount: defaultBankAccountResult.selectedBankAccount,
      iban: bankAccountDetails.iban,
      swift: bankAccountDetails.swift,
    },
    id: accountingDocument?.id,
    supplier: accountingDocument?.supplier,
    invoiceItems: isBalanceInvoiceFromCheckout
      ? mapInvoiceItems(balanceInvoiceItems, false)
      : mapInvoiceItems(
          accountingDocument?.items?.filter(
            (item) => item.generatedItemType !== 'proforma_deduction'
          ),
          true
        ),
    documentSeriesId: accountingDocument?.documentSeriesId,
    exchangeRateRatio: accountingDocument?.exchangeRateRatio,
    number: accountingDocument?.number,
    reason: null,
    isExchangeRateRatioEnabled: accountingDocument?.isExchangeRateRatioEnabled,
    dueSinceIssueDateInDays: accountingDocument?.dueSinceIssueDateInDays,
    footerNote: accountingDocument?.footerNote,
    isInternal: accountingDocument?.invoiceCategory === 'internal',
  };

  const isLoading = isLoadingInvoice || isLoadingBalanceItems;
  const isError = isInvoiceError || isBalanceItemsError;

  return (
    <DataStatus isLoading={isLoading} isError={isError} minHeight="80vh">
      <Helmet title={i18n.t('entity.correctiveTaxDocument.labels.newDocument')} />
      <CreateCorrectiveTaxDocumentForm
        defaultValues={defaultValues}
        handleFormSubmit={handleFormSubmit}
        dateOfTaxableSupply={accountingDocument?.dateOfTaxableSupply}
        supplier={accountingDocument?.supplier}
        isListOfItemsDisabled={
          isFromCheckout && isFull && invoiceType !== 'TAX_DOCUMENT_FOR_PAYMENT'
        }
        isFromCheckout={isFromCheckout}
        isFull={isFull}
        invoiceType={invoiceType}
      />
    </DataStatus>
  );
}
